"use client";

import type { UnifiedAgentViewState } from "../../core/types";
import { isUnifiedChatRoute, useUnifiedAgentViewState } from "../../hooks";
import { AgentHexIcon } from "@/components/sidebars/AgentHexIcon";
import { Button } from "@/components/ui/buttons/button";
import { useIsMobile } from "@/components/ui/hooks/use-mobile";
import {
  ResizeHandle,
  useResizableRailWidth,
} from "@/components/ui/layout/Page";
import { cn } from "@/lib/utils";
import { assertNever } from "@/packages/ts-utils";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { Suspense, useState, type CSSProperties } from "react";

const DockWindowContent = dynamic(
  () =>
    // eslint-disable-next-line custom/no-restricted-syntax -- the chat and report rendering stay out of every page's chunk until the dock first opens.
    import("./DockWindowContent").then((module) => module.DockWindowContent),
  { ssr: false }
);

type OpenViewState = Exclude<UnifiedAgentViewState, "closed">;

// The creation chat has its own agent; the unified agent edits studies on the study guide page.
const STUDY_EDIT_ROUTE = /^\/p\/[^/]+\/edit(\/|$)/;
const PANEL_WIDTH_PX = 440;
const PANEL_MIN_WIDTH_PX = 360;
const PANEL_MAX_WIDTH_PX = 800;
const FLOATING_WIDTH_PX = 400;
const FLOATING_MAX_HEIGHT_PX = 640;
const FLOATING_INSET_PX = 16;
// The gap and the window share one duration so the page and the panel move together.
const TRANSITION =
  "top 200ms ease-out, right 200ms ease-out, bottom 200ms ease-out, width 200ms ease-out, border-radius 200ms ease-out, opacity 200ms ease-out, transform 200ms ease-out";

const getWindowGeometry = ({
  mode,
  isMobile,
  panelWidth,
}: {
  mode: OpenViewState;
  isMobile: boolean;
  panelWidth: number;
}): CSSProperties => {
  if (isMobile) {
    return { top: 0, bottom: 0, right: 0, width: "100vw", borderRadius: 0 };
  }
  switch (mode) {
    case "floating":
      return {
        top: `max(${FLOATING_INSET_PX}px, calc(100svh - ${FLOATING_MAX_HEIGHT_PX + FLOATING_INSET_PX}px))`,
        bottom: FLOATING_INSET_PX,
        right: FLOATING_INSET_PX,
        width: FLOATING_WIDTH_PX,
        borderRadius: 16,
      };
    case "panel":
      return {
        top: 0,
        bottom: 0,
        right: 0,
        width: panelWidth,
        borderRadius: 0,
      };
    default:
      return assertNever(mode);
  }
};

const getClosedTransform = (mode: OpenViewState) => {
  switch (mode) {
    case "floating":
      return "translateY(8px) scale(0.96)";
    case "panel":
      return "translateX(100%)";
    default:
      return assertNever(mode);
  }
};

/** The active chat as a floating window or a right side panel, kept across navigation by the user layout. */
export const AgentDock = () => {
  const pathname = usePathname();
  const isMobile = useIsMobile({});
  const { unifiedAgentViewState, setUnifiedAgentViewState } =
    useUnifiedAgentViewState();
  const isOpen = unifiedAgentViewState !== "closed";
  // Keeps the last open geometry while closing, so the window leaves from where it was.
  const [displayMode, setDisplayMode] = useState<OpenViewState>("floating");
  if (
    unifiedAgentViewState !== "closed" &&
    unifiedAgentViewState !== displayMode
  ) {
    setDisplayMode(unifiedAgentViewState);
  }
  // After closing, the hidden window snaps to the floating geometry, so the next open rises from the bottom.
  const [hasCloseSettled, setHasCloseSettled] = useState(!isOpen);
  if (isOpen && hasCloseSettled) {
    setHasCloseSettled(false);
  }
  // Mounted on first open and kept after, so reopening is instant and keeps the scroll position.
  const [hasOpened, setHasOpened] = useState(isOpen);
  if (isOpen && !hasOpened) {
    setHasOpened(true);
  }

  const {
    width: panelWidth,
    isResizing,
    onResizeStart,
  } = useResizableRailWidth({
    defaultWidth: PANEL_WIDTH_PX,
    minWidth: PANEL_MIN_WIDTH_PX,
    maxWidth: PANEL_MAX_WIDTH_PX,
    side: "right",
  });

  // The full chat page replaces the dock without changing its view state, so the dock returns as it was on the next page.
  if (isUnifiedChatRoute(pathname) || STUDY_EDIT_ROUTE.test(pathname)) {
    return null;
  }

  const isPanelPushingPage = unifiedAgentViewState === "panel" && !isMobile;
  // Transitions would make the panel and the page trail behind the pointer.
  const windowTransition = hasCloseSettled || isResizing ? "none" : TRANSITION;

  return (
    <>
      <div
        aria-hidden
        className="hidden shrink-0 md:block"
        style={{
          width: isPanelPushingPage ? panelWidth : 0,
          transition: isResizing ? "none" : "width 200ms ease-out",
        }}
      />
      <div
        role="dialog"
        aria-label="Agent chat"
        aria-hidden={!isOpen}
        inert={!isOpen}
        className={cn(
          "fixed flex origin-bottom-right flex-col overflow-hidden border-surface-quatenary bg-surface-primary",
          displayMode === "panel" || isMobile
            ? "border-l"
            : "border shadow-floating",
          // Full screen on phones, so it must sit over the fixed mobile mark and sidebar trigger.
          isMobile ? "z-50" : "z-40",
          !isOpen && "pointer-events-none"
        )}
        style={{
          ...getWindowGeometry({ mode: displayMode, isMobile, panelWidth }),
          opacity: isOpen ? 1 : 0,
          transform: isOpen ? "none" : getClosedTransform(displayMode),
          transition: windowTransition,
        }}
        onTransitionEnd={(event) => {
          const isOwnOpacityEnd =
            event.target === event.currentTarget &&
            event.propertyName === "opacity";
          if (!isOpen && isOwnOpacityEnd) {
            setHasCloseSettled(true);
            setDisplayMode("floating");
          }
        }}
      >
        {hasOpened && (
          // The sidebar provider reads search params, which would otherwise opt the layout out of server rendering.
          <Suspense fallback={null}>
            <DockWindowContent isMobile={isMobile} />
          </Suspense>
        )}
      </div>
      {isPanelPushingPage && (
        // Outside the window, whose overflow would clip the half of the grab area past the seam.
        <div
          className="fixed inset-y-0 z-40 flex"
          style={{ right: panelWidth }}
        >
          <ResizeHandle onMouseDown={onResizeStart} />
        </div>
      )}
      <Button
        variant="outline"
        size="icon"
        aria-label="Open Agent chat"
        aria-hidden={isOpen}
        tabIndex={isOpen ? -1 : 0}
        onClick={() => setUnifiedAgentViewState("floating")}
        className={cn(
          "fixed bottom-4 right-4 z-40 size-11 rounded-full border-surface-tertiary bg-surface-primary shadow-floating transition-[transform,opacity] duration-200 ease-out hover:scale-105",
          isOpen
            ? "pointer-events-none scale-75 opacity-0"
            : "scale-100 opacity-100"
        )}
      >
        <AgentHexIcon className="size-5 text-content-primary" />
      </Button>
    </>
  );
};
